import React, { useState, useEffect, Suspense, lazy } from 'react';
import { SectionContent } from '../../Services/UserProjectService';
import { ProjectTreatment } from '../../hooks/useContentVariation';
import TextSimpleViewer from './TextSimpleViewer';
import TextSelectableViewer from './TextSelectableViewer';
import QuestionSimpleViewer from './QuestionSimpleViewer';
import QuestionMultipleViewer from './QuestionMultipleViewer';
import ImageCommentViewer from './ImageCommentViewer';
import { getChallengeTypeById } from '../../config/challengeTypes';

interface ContentViewerProps {
  currentContent: SectionContent | null;
  project?: ProjectTreatment;
  projectId?: string;
  onResponseChange?: (isValid: boolean, response: string, responseType: string) => void;
  navigationDirection?: 'forward' | 'backward';
}

const ContentViewer: React.FC<ContentViewerProps> = ({
  currentContent,
  project,
  projectId,
  onResponseChange,
  navigationDirection = 'forward'
}) => {
  const displayContent = currentContent;
  const [ChallengeComponent, setChallengeComponent] = useState<React.ComponentType<any> | null>(null);
  const [challengeLoadError, setChallengeLoadError] = useState<string | null>(null);

  // Load challenge component dynamically when needed
  useEffect(() => {
    if (displayContent && (displayContent as any).contentType === 'CHALLENGE') {
      const challengeType = (displayContent as any).content?.challengeType;

      if (!challengeType) {
        setChallengeLoadError('No se especificó el tipo de challenge');
        setChallengeComponent(null);
        return;
      }

      const challengeConfig = getChallengeTypeById(challengeType);

      if (!challengeConfig) {
        setChallengeLoadError(`Tipo de challenge desconocido: ${challengeType}`);
        setChallengeComponent(null);
        return;
      }

      // Dynamically import the challenge component
      const loadChallengeComponent = async () => {
        try {
          setChallengeLoadError(null);

          const componentModule = await import(`../../Pages/User/Challenges/${challengeConfig.componentName}`);
          const Component = componentModule.default;

          setChallengeComponent(() => Component);
        } catch (error) {
          console.error(`Error loading challenge component ${challengeConfig.componentName}:`, error);
          setChallengeLoadError(`Error cargando el challenge: ${challengeConfig.name}`);
          setChallengeComponent(null);
        }
      };

      loadChallengeComponent();
    } else {
      setChallengeComponent(null);
      setChallengeLoadError(null);
    }
  }, [displayContent]);
  
  if (!displayContent) {
    return (
      <div className="content-viewer text-center py-5">
        <div className="alert alert-info">
          No hay contenido disponible.
        </div>
      </div>
    );
  }

  // Check content types
  const contentType = (displayContent as any).contentType;
  const isTextSimple = contentType === 'TEXT_SIMPLE';
  const isTextSelectable = contentType === 'TEXT_SELECTABLE';
  const isQuestionSimple = contentType === 'QUESTION_SIMPLE';
  const isQuestionMultiple = contentType === 'QUESTION_MULTIPLE';
  const isChallenge = contentType === 'CHALLENGE';
  const isImageComment = contentType === 'IMAGE_COMMENT';

  return (
    <div className={`content-viewer ${isTextSimple || isTextSelectable || isQuestionSimple || isQuestionMultiple || isChallenge || isImageComment ? 'text-simple-viewer' : ''}`}>
      {!isTextSimple && !isTextSelectable && !isQuestionSimple && !isQuestionMultiple && !isChallenge && !isImageComment && (
        <div className="content-header mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {displayContent.isCompleted && (
                <span className="badge bg-success">
                  <i className="fas fa-check me-1"></i>
                  Completado
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="content-body mb-4">
        {isTextSimple ? (
          <TextSimpleViewer
            content={displayContent}
            project={project}
            navigationDirection={navigationDirection}
          />
        ) : isTextSelectable ? (
          <TextSelectableViewer
            content={displayContent}
            project={project}
            navigationDirection={navigationDirection}
          />
        ) : isQuestionSimple ? (
          <QuestionSimpleViewer
            key={`question-simple-${displayContent.id || 'new'}`}
            content={displayContent}
            project={project}
            navigationDirection={navigationDirection}
            onResponseChange={(isValid, response) => onResponseChange?.(isValid, response, 'text')}
          />
        ) : isQuestionMultiple ? (
          <QuestionMultipleViewer
            key={`question-multiple-${displayContent.id || 'new'}`}
            content={displayContent}
            project={project}
            navigationDirection={navigationDirection}
            onResponseChange={(isValid, response) => {
              const responseType = (displayContent as any).content?.allowMultiple ? 'multiple_choice' : 'single_choice';
              onResponseChange?.(isValid, response, responseType);
            }}
          />
        ) : isChallenge ? (
          <div className="challenge-container">
            {challengeLoadError ? (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {challengeLoadError}
              </div>
            ) : ChallengeComponent ? (
              <Suspense fallback={
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Cargando challenge...</span>
                  </div>
                  <div className="mt-2">Cargando challenge...</div>
                </div>
              }>
                <ChallengeComponent
                  content={displayContent}
                  project={project}
                  projectId={projectId}
                  onResponseChange={(isValid: boolean, response: string) =>
                    onResponseChange?.(isValid, response, 'challenge')
                  }
                />
              </Suspense>
            ) : (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Cargando challenge...</span>
                </div>
                <div className="mt-2">Cargando challenge...</div>
              </div>
            )}
          </div>
        ) : isImageComment ? (
          <ImageCommentViewer
            key={`image-comment-${displayContent.id || 'new'}`}
            content={displayContent}
            project={project}
            projectId={projectId}
            navigationDirection={navigationDirection}
            onResponseChange={(isValid, response) => onResponseChange?.(isValid, response, 'text')}
          />
        ) : (
          <div className="card">
            <div className="card-header">
              <strong>Contenido JSON:</strong>
            </div>
            <div className="card-body">
              <pre className="bg-light p-3 rounded" style={{ fontSize: '0.9rem' }}>
                {JSON.stringify(displayContent, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ContentViewer;